// src/utils/sessionManagement.js
import { doc, updateDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { logSecurityEvent } from './securityMonitoring.js';

// Anti-abuse session configuration
const SESSION_CONFIG = {
  // Extended timeouts for game sessions
  MAX_SESSION_DURATION: 24 * 60 * 60 * 1000, // 24 hours
  INACTIVITY_TIMEOUT: 30 * 60 * 1000, // 30 minutes (normal)
  GAME_INACTIVITY_TIMEOUT: 3 * 60 * 60 * 1000, // 3 hours (during games)
  HOST_INACTIVITY_TIMEOUT: 4 * 60 * 60 * 1000, // 4 hours (for hosts)
  
  WARNING_BEFORE_TIMEOUT: 10 * 60 * 1000, // 10 minutes warning
  GAME_WARNING_BEFORE_TIMEOUT: 30 * 60 * 1000, // 30 minutes warning during games
  
  HEARTBEAT_INTERVAL: 5 * 60 * 1000, // 5 minutes
  
  // ANTI-ABUSE SETTINGS
  MAX_CONCURRENT_SESSIONS: 1, // Only ONE session per user (prevents account sharing)
  MAX_ACTIVE_GAMES_PER_USER: 1, // Only ONE active game per user (prevents multiple venues)
  MAX_IP_CHANGES_PER_SESSION: 2, // Max IP changes per session (prevents VPN abuse)
  MAX_DEVICE_CHANGES_PER_SESSION: 1, // Max device changes per session
  SESSION_SUSPICION_THRESHOLD: 3, // Number of suspicious activities before flagging
  
  // Game state detection
  GAME_ACTIVITY_THRESHOLD: 5 * 60 * 1000, // 5 minutes of game activity
  MIN_GAME_DURATION: 30 * 60 * 1000, // 30 minutes minimum game time
  
  SECURE_COOKIE_OPTIONS: {
    httpOnly: true,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict',
    maxAge: 24 * 60 * 60 * 1000 // 24 hours
  }
};

// Session tracking
const activeSessions = new Map();
const sessionHeartbeats = new Map();
const gameSessions = new Map(); // Track users in active games
const abuseTracking = new Map(); // Track potential abuse patterns

/**
 * Initialize secure session for user with anti-abuse protection
 * @param {string} userId - User ID
 * @param {Object} sessionData - Session data
 * @returns {Promise<Object>} Session initialization result
 */
export const initializeSecureSession = async (userId, sessionData) => {
  try {
    const sessionId = generateSessionId();
    const now = Date.now();
    
    // Check for existing sessions
    const existingSessions = activeSessions.get(userId) || [];
    
    // ANTI-ABUSE: Only allow ONE session per user
    if (existingSessions.length >= SESSION_CONFIG.MAX_CONCURRENT_SESSIONS) {
      // Log potential abuse
      await logSecurityEvent('concurrent_session_attempt', {
        userId,
        existingSessionsCount: existingSessions.length,
        newSessionIP: sessionData.ipAddress,
        newSessionUserAgent: sessionData.userAgent,
        timestamp: now
      }, userId, 'high');
      
      // Terminate ALL existing sessions (prevent account sharing)
      for (const session of existingSessions) {
        await terminateSession(userId, session.sessionId);
      }
      existingSessions.length = 0;
    }
    
    // Check for abuse patterns
    const abuseScore = await checkAbusePatterns(userId, sessionData);
    if (abuseScore > SESSION_CONFIG.SESSION_SUSPICION_THRESHOLD) {
      await logSecurityEvent('high_abuse_score_session', {
        userId,
        abuseScore,
        ipAddress: sessionData.ipAddress,
        userAgent: sessionData.userAgent,
        deviceInfo: getDeviceInfo()
      }, userId, 'critical');
      
      // Still allow session but flag for monitoring
      sessionData.isFlagged = true;
    }
    
    const session = {
      sessionId,
      userId,
      createdAt: now,
      lastActivity: now,
      lastGameActivity: null,
      ipAddress: sessionData.ipAddress,
      userAgent: sessionData.userAgent,
      location: sessionData.location,
      deviceInfo: getDeviceInfo(),
      deviceFingerprint: generateDeviceFingerprint(sessionData),
      isActive: true,
      isInGame: false,
      gameId: null,
      userRole: sessionData.userRole || 'player',
      isFlagged: sessionData.isFlagged || false,
      ipChanges: 0,
      deviceChanges: 0,
      abuseScore: abuseScore
    };
    
    // Store session
    existingSessions.push(session);
    activeSessions.set(userId, existingSessions);
    
    // Start heartbeat
    startSessionHeartbeat(userId, sessionId);
    
    // Log session creation
    await logSecurityEvent('session_created', {
      sessionId,
      userId,
      ipAddress: sessionData.ipAddress,
      userAgent: sessionData.userAgent,
      deviceInfo: session.deviceInfo,
      userRole: session.userRole,
      abuseScore: abuseScore,
      isFlagged: session.isFlagged
    }, userId, abuseScore > 2 ? 'high' : 'low');
    
    // Update user's last login - handle case where user document might not exist yet
    try {
      await updateDoc(doc(db, 'users', userId), {
        lastLoginAt: serverTimestamp(),
        lastLoginIP: sessionData.ipAddress,
        lastUserAgent: sessionData.userAgent,
        lastLoginLocation: sessionData.location,
        activeSessions: existingSessions.length,
        lastAbuseScore: abuseScore,
        isSessionFlagged: session.isFlagged
      });
    } catch (error) {
      console.warn('Could not update user document (may not exist yet):', error.message);
      
      // Try to create a minimal user document if it doesn't exist
      try {
        const { setDoc } = await import('firebase/firestore');
        await setDoc(doc(db, 'users', userId), {
          uid: userId,
          role: 'host', // Default role
          plan: 'host', // Default plan
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          lastLoginIP: sessionData.ipAddress,
          lastUserAgent: sessionData.userAgent,
          lastLoginLocation: sessionData.location,
          activeSessions: existingSessions.length,
          lastAbuseScore: abuseScore,
          isSessionFlagged: session.isFlagged,
          accountStatus: 'active'
        });
        console.log('Created minimal user document for:', userId);
      } catch (createError) {
        console.warn('Could not create user document either:', createError.message);
        // Continue with session creation even if user document creation fails
      }
    }
    
    return {
      sessionId,
      expiresAt: now + SESSION_CONFIG.MAX_SESSION_DURATION,
      inactivityTimeout: getInactivityTimeout(session.userRole, false),
      isFlagged: session.isFlagged,
      abuseScore: abuseScore
    };
    
  } catch (error) {
    console.error('Error initializing session:', error);
    throw error;
  }
};

/**
 * Check for abuse patterns
 * @param {string} userId - User ID
 * @param {Object} sessionData - Session data
 * @returns {Promise<number>} Abuse score (0-10)
 */
const checkAbusePatterns = async (userId, sessionData) => {
  let abuseScore = 0;
  
  try {
    // Get user's session history - handle case where user document might not exist yet
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.exists() ? userDoc.data() : null;
    
    if (!userData) {
      console.log('User document not found, skipping abuse pattern checks for new user:', userId);
      return 0;
    }
    
    // Check for rapid IP changes
    if (userData.lastLoginIP && userData.lastLoginIP !== sessionData.ipAddress) {
      const timeSinceLastLogin = Date.now() - (userData.lastLoginAt?.toMillis() || 0);
      if (timeSinceLastLogin < 5 * 60 * 1000) { // 5 minutes
        abuseScore += 3; // Rapid IP change
      }
    }
    
    // Check for device fingerprint changes
    if (userData.lastDeviceFingerprint) {
      const currentFingerprint = generateDeviceFingerprint(sessionData);
      if (userData.lastDeviceFingerprint !== currentFingerprint) {
        abuseScore += 2; // Device change
      }
    }
    
    // Check for multiple recent sessions
    const recentSessions = userData.recentSessions || [];
    const veryRecentSessions = recentSessions.filter(session => 
      Date.now() - session.timestamp < 30 * 60 * 1000 // 30 minutes
    );
    
    if (veryRecentSessions.length > 3) {
      abuseScore += 2; // Too many recent sessions
    }
    
    // Check for suspicious user agent patterns
    const userAgent = sessionData.userAgent.toLowerCase();
    if (userAgent.includes('bot') || userAgent.includes('crawler') || userAgent.includes('spider')) {
      abuseScore += 5; // Bot detection
    }
    
    // Check for VPN/proxy indicators
    if (sessionData.ipAddress && (
      sessionData.ipAddress.includes('vpn') || 
      sessionData.ipAddress.includes('proxy') ||
      sessionData.ipAddress.includes('tor')
    )) {
      abuseScore += 1; // VPN/proxy usage
    }
    
    // Check abuse tracking
    const userAbuse = abuseTracking.get(userId) || { violations: 0, lastViolation: 0 };
    if (userAbuse.violations > 0) {
      abuseScore += userAbuse.violations;
    }
    
  } catch (error) {
    console.error('Error checking abuse patterns:', error);
  }
  
  return Math.min(abuseScore, 10); // Cap at 10
};

/**
 * Generate device fingerprint
 * @param {Object} sessionData - Session data
 * @returns {string} Device fingerprint
 */
const generateDeviceFingerprint = (sessionData) => {
  const deviceInfo = getDeviceInfo();
  const fingerprint = [
    deviceInfo.platform,
    deviceInfo.language,
    deviceInfo.screenResolution,
    deviceInfo.timezone,
    deviceInfo.cookieEnabled,
    sessionData.userAgent
  ].join('|');
  
  // Simple hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString(36);
};

/**
 * Get appropriate inactivity timeout based on user role and game state
 * @param {string} userRole - User role (host, player, admin)
 * @param {boolean} isInGame - Whether user is in active game
 * @returns {number} Inactivity timeout in milliseconds
 */
const getInactivityTimeout = (userRole, isInGame) => {
  if (isInGame) {
    return SESSION_CONFIG.GAME_INACTIVITY_TIMEOUT;
  }
  
  switch (userRole) {
    case 'host':
      return SESSION_CONFIG.HOST_INACTIVITY_TIMEOUT;
    case 'admin':
      return SESSION_CONFIG.MAX_SESSION_DURATION; // Admins get full session
    default:
      return SESSION_CONFIG.INACTIVITY_TIMEOUT;
  }
};

/**
 * Mark user as entering a game with anti-abuse protection
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @param {string} gameId - Game ID
 * @param {string} userRole - User role in game
 * @returns {Promise<boolean>} Success status
 */
export const enterGameSession = async (userId, sessionId, gameId, userRole = 'player') => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    const session = userSessions.find(s => s.sessionId === sessionId);
    
    if (!session || !session.isActive) {
      return false;
    }
    
    // ANTI-ABUSE: Check if user is already in another game
    const existingGameSession = gameSessions.get(userId);
    if (existingGameSession && existingGameSession.gameId !== gameId) {
      // Log potential abuse - user trying to run multiple games
      await logSecurityEvent('multiple_game_attempt', {
        userId,
        existingGameId: existingGameSession.gameId,
        newGameId: gameId,
        sessionId,
        timestamp: Date.now()
      }, userId, 'critical');
      
      // Block the new game session
      return false;
    }
    
    // ANTI-ABUSE: Check for rapid game switching
    const recentGames = session.recentGames || [];
    const veryRecentGames = recentGames.filter(game => 
      Date.now() - game.timestamp < 10 * 60 * 1000 // 10 minutes
    );
    
    if (veryRecentGames.length > 2) {
      await logSecurityEvent('rapid_game_switching', {
        userId,
        gameId,
        recentGamesCount: veryRecentGames.length,
        timestamp: Date.now()
      }, userId, 'high');
    }
    
    // Update session for game mode
    session.isInGame = true;
    session.gameId = gameId;
    session.lastGameActivity = Date.now();
    session.userRole = userRole;
    session.lastActivity = Date.now();
    
    // Track recent games
    session.recentGames = [
      { gameId, timestamp: Date.now() },
      ...(session.recentGames || []).slice(0, 4) // Keep last 5 games
    ];
    
    // Track game session
    gameSessions.set(userId, {
      gameId,
      sessionId,
      enteredAt: Date.now(),
      userRole,
      ipAddress: session.ipAddress,
      deviceFingerprint: session.deviceFingerprint
    });
    
    // Log game entry
    await logSecurityEvent('game_session_started', {
      sessionId,
      userId,
      gameId,
      userRole,
      isFlagged: session.isFlagged,
      abuseScore: session.abuseScore
    }, userId, session.isFlagged ? 'high' : 'low');
    
    return true;
    
  } catch (error) {
    console.error('Error entering game session:', error);
    return false;
  }
};

/**
 * Mark user as leaving a game
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Success status
 */
export const exitGameSession = async (userId, sessionId) => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    const session = userSessions.find(s => s.sessionId === sessionId);
    
    if (!session) {
      return false;
    }
    
    // Update session to exit game mode
    session.isInGame = false;
    session.gameId = null;
    session.lastActivity = Date.now();
    
    // Remove from game sessions
    gameSessions.delete(userId);
    
    // Log game exit
    await logSecurityEvent('game_session_ended', {
      sessionId,
      userId,
      gameId: session.gameId,
      duration: Date.now() - (session.lastGameActivity || session.createdAt)
    }, userId, 'low');
    
    return true;
    
  } catch (error) {
    console.error('Error exiting game session:', error);
    return false;
  }
};

/**
 * Update game activity (called during game interactions)
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Success status
 */
export const updateGameActivity = async (userId, sessionId) => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    const session = userSessions.find(s => s.sessionId === sessionId);
    
    if (!session || !session.isActive) {
      return false;
    }
    
    // Update both general and game activity
    session.lastActivity = Date.now();
    if (session.isInGame) {
      session.lastGameActivity = Date.now();
    }
    
    return true;
    
  } catch (error) {
    console.error('Error updating game activity:', error);
    return false;
  }
};

/**
 * Validate and refresh session (game-aware)
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Session validity
 */
export const validateSession = async (userId, sessionId) => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    const session = userSessions.find(s => s.sessionId === sessionId);
    
    if (!session || !session.isActive) {
      return false;
    }
    
    const now = Date.now();
    
    // Check session expiration
    if (now - session.createdAt > SESSION_CONFIG.MAX_SESSION_DURATION) {
      await terminateSession(userId, sessionId);
      return false;
    }
    
    // Get appropriate timeout based on role and game state
    const inactivityTimeout = getInactivityTimeout(session.userRole, session.isInGame);
    
    // Check inactivity timeout
    if (now - session.lastActivity > inactivityTimeout) {
      // Special handling for game sessions - show warning instead of immediate termination
      if (session.isInGame) {
        console.warn(`User ${userId} inactive in game ${session.gameId}, but keeping session active`);
        // Don't terminate during games, just log the inactivity
        await logSecurityEvent('game_session_inactive', {
          sessionId,
          userId,
          gameId: session.gameId,
          inactiveDuration: now - session.lastActivity
        }, userId, 'medium');
        return true; // Keep session active during games
      } else {
        await terminateSession(userId, sessionId);
        return false;
      }
    }
    
    // Update last activity
    session.lastActivity = now;
    
    // Update heartbeat
    const heartbeat = sessionHeartbeats.get(sessionId);
    if (heartbeat) {
      heartbeat.lastActivity = now;
    }
    
    return true;
    
  } catch (error) {
    console.error('Error validating session:', error);
    return false;
  }
};

/**
 * Terminate session
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Termination result
 */
export const terminateSession = async (userId, sessionId) => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    const sessionIndex = userSessions.findIndex(s => s.sessionId === sessionId);
    
    if (sessionIndex === -1) {
      return false;
    }
    
    const session = userSessions[sessionIndex];
    session.isActive = false;
    session.terminatedAt = Date.now();
    
    // Remove from active sessions
    userSessions.splice(sessionIndex, 1);
    activeSessions.set(userId, userSessions);
    
    // Stop heartbeat
    stopSessionHeartbeat(sessionId);
    
    // Log session termination
    await logSecurityEvent('session_terminated', {
      sessionId,
      userId,
      duration: session.terminatedAt - session.createdAt,
      reason: 'manual_termination'
    }, userId, 'low');
    
    return true;
    
  } catch (error) {
    console.error('Error terminating session:', error);
    return false;
  }
};

/**
 * Terminate all user sessions
 * @param {string} userId - User ID
 * @returns {Promise<number>} Number of sessions terminated
 */
export const terminateAllUserSessions = async (userId) => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    let terminatedCount = 0;
    
    for (const session of userSessions) {
      if (session.isActive) {
        session.isActive = false;
        session.terminatedAt = Date.now();
        stopSessionHeartbeat(session.sessionId);
        terminatedCount++;
      }
    }
    
    // Clear all sessions
    activeSessions.set(userId, []);
    
    // Log bulk termination
    await logSecurityEvent('all_sessions_terminated', {
      userId,
      terminatedCount,
      reason: 'admin_action'
    }, userId, 'medium');
    
    return terminatedCount;
    
  } catch (error) {
    console.error('Error terminating all sessions:', error);
    return 0;
  }
};

/**
 * Get user's active sessions
 * @param {string} userId - User ID
 * @returns {Array} Active sessions
 */
export const getUserActiveSessions = (userId) => {
  const userSessions = activeSessions.get(userId) || [];
  return userSessions.filter(session => session.isActive);
};

/**
 * Start session heartbeat
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 */
const startSessionHeartbeat = (userId, sessionId) => {
  const heartbeat = {
    userId,
    sessionId,
    lastActivity: Date.now(),
    interval: setInterval(async () => {
      try {
        const isValid = await validateSession(userId, sessionId);
        if (!isValid) {
          stopSessionHeartbeat(sessionId);
        }
      } catch (error) {
        console.error('Session heartbeat error:', error);
        stopSessionHeartbeat(sessionId);
      }
    }, SESSION_CONFIG.HEARTBEAT_INTERVAL)
  };
  
  sessionHeartbeats.set(sessionId, heartbeat);
};

/**
 * Stop session heartbeat
 * @param {string} sessionId - Session ID
 */
const stopSessionHeartbeat = (sessionId) => {
  const heartbeat = sessionHeartbeats.get(sessionId);
  if (heartbeat && heartbeat.interval) {
    clearInterval(heartbeat.interval);
    sessionHeartbeats.delete(sessionId);
  }
};

/**
 * Generate secure session ID
 * @returns {string} Session ID
 */
const generateSessionId = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Get device information
 * @returns {Object} Device info
 */
const getDeviceInfo = () => {
  return {
    platform: navigator.platform,
    language: navigator.language,
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack
  };
};

/**
 * Set secure session cookie
 * @param {string} sessionId - Session ID
 * @param {number} maxAge - Cookie max age
 */
export const setSecureSessionCookie = (sessionId, maxAge = SESSION_CONFIG.SECURE_COOKIE_OPTIONS.maxAge) => {
  const cookieValue = `session=${sessionId}; Max-Age=${maxAge / 1000}; Path=/; HttpOnly; Secure; SameSite=Strict`;
  document.cookie = cookieValue;
};

/**
 * Get session cookie
 * @returns {string|null} Session ID from cookie
 */
export const getSessionCookie = () => {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session') {
      return value;
    }
  }
  return null;
};

/**
 * Clear session cookie
 */
export const clearSessionCookie = () => {
  document.cookie = 'session=; Max-Age=0; Path=/; HttpOnly; Secure; SameSite=Strict';
};

/**
 * Check for session timeout warning (game-aware)
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @returns {Object} Timeout warning status
 */
export const checkSessionTimeoutWarning = (userId, sessionId) => {
  const userSessions = activeSessions.get(userId) || [];
  const session = userSessions.find(s => s.sessionId === sessionId);
  
  if (!session) {
    return { shouldWarn: false, timeRemaining: 0, isGameSession: false };
  }
  
  const timeSinceLastActivity = Date.now() - session.lastActivity;
  const inactivityTimeout = getInactivityTimeout(session.userRole, session.isInGame);
  const warningThreshold = session.isInGame ? 
    SESSION_CONFIG.GAME_WARNING_BEFORE_TIMEOUT : 
    SESSION_CONFIG.WARNING_BEFORE_TIMEOUT;
  
  const timeRemaining = inactivityTimeout - timeSinceLastActivity;
  
  return {
    shouldWarn: timeRemaining <= warningThreshold && timeRemaining > 0,
    timeRemaining: Math.max(0, timeRemaining),
    isGameSession: session.isInGame,
    gameId: session.gameId,
    userRole: session.userRole
  };
};

/**
 * Extend session activity
 * @param {string} userId - User ID
 * @param {string} sessionId - Session ID
 * @returns {Promise<boolean>} Extension result
 */
export const extendSessionActivity = async (userId, sessionId) => {
  try {
    const userSessions = activeSessions.get(userId) || [];
    const session = userSessions.find(s => s.sessionId === sessionId);
    
    if (!session || !session.isActive) {
      return false;
    }
    
    session.lastActivity = Date.now();
    
    // Update heartbeat
    const heartbeat = sessionHeartbeats.get(sessionId);
    if (heartbeat) {
      heartbeat.lastActivity = Date.now();
    }
    
    return true;
    
  } catch (error) {
    console.error('Error extending session activity:', error);
    return false;
  }
};

/**
 * Get session statistics
 * @param {string} userId - User ID
 * @returns {Object} Session statistics
 */
export const getSessionStatistics = (userId) => {
  const userSessions = activeSessions.get(userId) || [];
  const activeSessionsCount = userSessions.filter(s => s.isActive).length;
  const totalSessions = userSessions.length;
  
  return {
    activeSessions: activeSessionsCount,
    totalSessions,
    maxConcurrentSessions: SESSION_CONFIG.MAX_CONCURRENT_SESSIONS,
    sessionDuration: SESSION_CONFIG.MAX_SESSION_DURATION,
    inactivityTimeout: getInactivityTimeout(userSessions.find(s => s.isActive)?.userRole || 'player', false) // Get timeout for the last active session
  };
};

/**
 * Get game session status
 * @param {string} userId - User ID
 * @returns {Object|null} Game session info
 */
export const getGameSessionStatus = (userId) => {
  return gameSessions.get(userId) || null;
};

/**
 * Get all active game sessions
 * @returns {Array} Active game sessions
 */
export const getAllActiveGameSessions = () => {
  return Array.from(gameSessions.entries()).map(([userId, gameSession]) => ({
    userId,
    ...gameSession
  }));
};

/**
 * Force exit all users from a specific game
 * @param {string} gameId - Game ID
 * @returns {Promise<number>} Number of users exited
 */
export const forceExitGameSession = async (gameId) => {
  try {
    let exitedCount = 0;
    
    for (const [userId, gameSession] of gameSessions.entries()) {
      if (gameSession.gameId === gameId) {
        await exitGameSession(userId, gameSession.sessionId);
        exitedCount++;
      }
    }
    
    return exitedCount;
    
  } catch (error) {
    console.error('Error forcing exit from game session:', error);
    return 0;
  }
};

/**
 * ANTI-ABUSE: Flag user for suspicious activity
 * @param {string} userId - User ID
 * @param {string} reason - Reason for flagging
 * @param {string} severity - low, medium, high, critical
 * @returns {Promise<boolean>} Success status
 */
export const flagUserForAbuse = async (userId, reason, severity = 'medium') => {
  try {
    const userAbuse = abuseTracking.get(userId) || { violations: 0, lastViolation: 0, reasons: [] };
    userAbuse.violations += 1;
    userAbuse.lastViolation = Date.now();
    userAbuse.reasons.push({ reason, severity, timestamp: Date.now() });
    
    // Keep only last 10 violations
    userAbuse.reasons = userAbuse.reasons.slice(-10);
    
    abuseTracking.set(userId, userAbuse);
    
    // Log the violation
    await logSecurityEvent('user_flagged_for_abuse', {
      userId,
      reason,
      severity,
      totalViolations: userAbuse.violations,
      timestamp: Date.now()
    }, userId, severity);
    
    // Update user document
    await updateDoc(doc(db, 'users', userId), {
      abuseViolations: userAbuse.violations,
      lastAbuseViolation: serverTimestamp(),
      isFlaggedForAbuse: true
    });
    
    return true;
    
  } catch (error) {
    console.error('Error flagging user for abuse:', error);
    return false;
  }
};

/**
 * ANTI-ABUSE: Get abuse statistics for user
 * @param {string} userId - User ID
 * @returns {Object} Abuse statistics
 */
export const getUserAbuseStats = (userId) => {
  const userAbuse = abuseTracking.get(userId) || { violations: 0, lastViolation: 0, reasons: [] };
  return {
    violations: userAbuse.violations,
    lastViolation: userAbuse.lastViolation,
    reasons: userAbuse.reasons,
    isFlagged: userAbuse.violations > 0
  };
};

/**
 * ANTI-ABUSE: Check if user is currently in multiple games
 * @param {string} userId - User ID
 * @returns {Array} Active games for user
 */
export const getUserActiveGames = (userId) => {
  const gameSession = gameSessions.get(userId);
  return gameSession ? [gameSession] : [];
};

/**
 * ANTI-ABUSE: Get all users with multiple active games (potential abuse)
 * @returns {Array} Users with multiple games
 */
export const getUsersWithMultipleGames = () => {
  const suspiciousUsers = [];
  
  for (const [userId] of gameSessions.entries()) {
    const userSessions = activeSessions.get(userId) || [];
    const activeGames = userSessions.filter(s => s.isInGame && s.isActive);
    
    if (activeGames.length > 1) {
      suspiciousUsers.push({
        userId,
        activeGames: activeGames.map(s => ({
          gameId: s.gameId,
          sessionId: s.sessionId,
          startedAt: s.lastGameActivity
        }))
      });
    }
  }
  
  return suspiciousUsers;
};

/**
 * ANTI-ABUSE: Suspend user account for abuse
 * @param {string} userId - User ID
 * @param {string} reason - Suspension reason
 * @param {number} durationHours - Suspension duration in hours
 * @returns {Promise<boolean>} Success status
 */
export const suspendUserAccount = async (userId, reason, durationHours = 24) => {
  try {
    // Terminate all active sessions
    await terminateAllUserSessions(userId);
    
    // Update user document
    await updateDoc(doc(db, 'users', userId), {
      isSuspended: true,
      suspensionReason: reason,
      suspendedAt: serverTimestamp(),
      suspensionExpiresAt: new Date(Date.now() + (durationHours * 60 * 60 * 1000)),
      activeSessions: 0
    });
    
    // Log suspension
    await logSecurityEvent('user_account_suspended', {
      userId,
      reason,
      durationHours,
      timestamp: Date.now()
    }, userId, 'critical');
    
    return true;
    
  } catch (error) {
    console.error('Error suspending user account:', error);
    return false;
  }
};

/**
 * ANTI-ABUSE: Check if user account is suspended
 * @param {string} userId - User ID
 * @returns {Promise<Object|null>} Suspension info or null
 */
export const checkUserSuspension = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userData = userDoc.data();
    
    if (userData?.isSuspended) {
      const expiresAt = userData.suspensionExpiresAt?.toMillis();
      const isExpired = expiresAt && Date.now() > expiresAt;
      
      if (isExpired) {
        // Auto-unsuspend
        await updateDoc(doc(db, 'users', userId), {
          isSuspended: false,
          suspensionReason: null,
          suspendedAt: null,
          suspensionExpiresAt: null
        });
        return null;
      }
      
      return {
        reason: userData.suspensionReason,
        suspendedAt: userData.suspendedAt,
        expiresAt: userData.suspensionExpiresAt,
        isExpired: false
      };
    }
    
    return null;
    
  } catch (error) {
    console.error('Error checking user suspension:', error);
    return null;
  }
};

/**
 * Clean up expired sessions
 */
export const cleanupExpiredSessions = async () => {
  try {
    const now = Date.now();
    let cleanedCount = 0;
    
    for (const [userId, userSessions] of activeSessions.entries()) {
      // eslint-disable-next-line no-loop-func
      userSessions.filter(session => {
        const isExpired = now - session.createdAt > SESSION_CONFIG.MAX_SESSION_DURATION ||
                         (session.lastActivity && now - session.lastActivity > getInactivityTimeout(session.userRole, session.isInGame));
        
        if (isExpired && session.isActive) {
          session.isActive = false;
          session.terminatedAt = now;
          stopSessionHeartbeat(session.sessionId);
          cleanedCount++;
        }
        
        return isExpired;
      });
      
      // Remove expired sessions
      const activeSessionsOnly = userSessions.filter(s => s.isActive);
      activeSessions.set(userId, activeSessionsOnly);
    }
    
    if (cleanedCount > 0) {
      console.log(`Cleaned up ${cleanedCount} expired sessions`);
    }
    
  } catch (error) {
    console.error('Error cleaning up expired sessions:', error);
  }
};

// Run cleanup every hour
setInterval(cleanupExpiredSessions, 60 * 60 * 1000); 